import axios from 'axios'
import type { RegionEntry } from './regionConfig'

export type IdentifierType = 'clientCode' | 'hostname' | 'positionUuid' | 'clientUuid' | 'apCode' | 'career'

export interface RegionResult {
  region: string
  apiUrl: string
  probeData: unknown
}

interface CacheEntry {
  region: string
  apiUrl: string
  expiry: number
}

const CACHE_TTL_MS = 24 * 60 * 60 * 1000 // 24 hours
const PROBE_TIMEOUT_MS = 5000

function cacheKey(type: IdentifierType, identifier: string): string {
  return `orbio_region_${type}_${identifier}`
}

function readCache(type: IdentifierType, identifier: string): CacheEntry | null {
  try {
    const raw = localStorage.getItem(cacheKey(type, identifier))
    if (!raw) return null
    const entry: CacheEntry = JSON.parse(raw)
    if (Date.now() > entry.expiry) {
      localStorage.removeItem(cacheKey(type, identifier))
      return null
    }
    return entry
  } catch {
    return null
  }
}

function writeCache(type: IdentifierType, identifier: string, region: string, apiUrl: string): void {
  const entry: CacheEntry = { region, apiUrl, expiry: Date.now() + CACHE_TTL_MS }
  try {
    localStorage.setItem(cacheKey(type, identifier), JSON.stringify(entry))
  } catch {
    // localStorage full or unavailable — continue without caching
  }
}

function getProbeConfig(
  type: IdentifierType,
  identifier: string,
  probeParams?: Record<string, string>,
): { path: string; params?: Record<string, string> } {
  switch (type) {
    case 'clientCode':
      return { path: `/hiring/${identifier}/positions-public` }
    case 'hostname':
      return { path: '/hiring/by-domain/positions-public', params: { domain: identifier } }
    case 'positionUuid':
      return { path: `/hiring/positions/${identifier}` }
    case 'clientUuid':
      // Probe by client UUID. The public client-by-code endpoint (/account/clients/{code}/public)
      // looks up by `code`, not `uuid`, so it always 404s for UUIDs. Use the internal-apply config
      // endpoint instead: it takes a UUID and 200s when the client has the internal or referral
      // portal enabled — the only contexts in which we carry a client UUID through the URL.
      return { path: `/hiring/internal/${identifier}/config` }
    case 'apCode':
      return { path: `/hiring/apply-sites/${identifier}/positions-public` }
    case 'career':
      // A career site, by slug (`site`) or by host (`host`), plus the page `path`.
      // The caller passes the query; the identifier only keys the cache.
      return { path: '/career/page', params: probeParams }
  }
}

function is404(error: unknown): boolean {
  const err = error as any
  return err?.response?.status === 404
}

export async function resolveRegion(
  identifier: string,
  type: IdentifierType,
  regionOrder: RegionEntry[],
  apiKey: string,
  probeParams?: Record<string, string>,
): Promise<RegionResult> {
  const cached = readCache(type, identifier)
  if (cached) {
    return { region: cached.region, apiUrl: cached.apiUrl, probeData: null }
  }

  const probe = getProbeConfig(type, identifier, probeParams)
  let all404 = true

  for (const entry of regionOrder) {
    try {
      const client = axios.create({
        baseURL: entry.url,
        timeout: PROBE_TIMEOUT_MS,
        headers: {
          'x-api-key': apiKey,
          'x-orbio-region-probe': '1',
        },
      })

      const response = probe.params
        ? await client.get(probe.path, { params: probe.params })
        : await client.get(probe.path)

      writeCache(type, identifier, entry.key, entry.url)
      return { region: entry.key, apiUrl: entry.url, probeData: response.data }
    } catch (error) {
      if (!is404(error)) {
        all404 = false
      }
    }
  }

  if (all404) {
    throw new Error('not-found')
  }
  throw new Error('network-error')
}

export function clearRegionCache(type: IdentifierType, identifier: string): void {
  localStorage.removeItem(cacheKey(type, identifier))
}
